import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  addSuccessStory,
  deleteSuccessStory,
  getSuccessStories,
  toggleSuccessStoryStatus,
  updateSuccessStory,
} from "./successStoriesApi";

export const FILTER_ALL = "all";
export const FILTER_SUCCESS_STORIES = "success_stories";
export const FILTER_PROGRAM_TESTIMONIALS = "program_testimonials";

type FilterValue =
  | typeof FILTER_ALL
  | typeof FILTER_SUCCESS_STORIES
  | typeof FILTER_PROGRAM_TESTIMONIALS;

interface SuccessStoriesState {
  filter: FilterValue;
  search: string;
  loading: { get: boolean; add: boolean; delete: boolean; toggle: boolean };
  req_success: { get: boolean; add: boolean; delete: boolean };
  all_stories: any[];
  stories: any[];
  edit: boolean;
  single: any | null;
}

const initialState: SuccessStoriesState = {
  filter: FILTER_ALL,
  search: "",
  loading: { get: false, add: false, delete: false, toggle: false },
  req_success: { get: false, add: false, delete: false },
  all_stories: [],
  stories: [],
  edit: false,
  single: null,
};

const applyFilters = (state: SuccessStoriesState) => {
  let list = state.all_stories;

  if (state.filter === FILTER_SUCCESS_STORIES) {
    list = list.filter((s) => s.show_on_success_stories);
  } else if (state.filter === FILTER_PROGRAM_TESTIMONIALS) {
    list = list.filter((s) => s.show_on_program_testimonials);
  }

  if (state.search.trim().length > 0) {
    const q = state.search.toLowerCase();
    list = list.filter((s) =>
      (s.name || "").toLowerCase().includes(q)
    );
  }

  state.stories = list;
};

const successStoriesSlice = createSlice({
  name: "successStories",
  initialState,
  reducers: {
    setFilter: (state, { payload }: PayloadAction<FilterValue>) => {
      state.filter = payload;
      applyFilters(state);
    },
    setSearch: (state, { payload }: PayloadAction<string>) => {
      state.search = payload;
      applyFilters(state);
    },
    editStory: (state, { payload }: PayloadAction<any>) => {
      state.edit = true;
      state.single = payload;
    },
    closeEditModal: (state) => {
      state.edit = false;
      state.single = null;
    },
    reorderStoriesLocal: (
      state,
      { payload }: PayloadAction<{ sourceIndex: number; destinationIndex: number }>
    ) => {
      const moved = state.stories[payload.sourceIndex];
      state.stories.splice(payload.sourceIndex, 1);
      state.stories.splice(payload.destinationIndex, 0, moved);
      state.all_stories = [...state.stories];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSuccessStories.pending, (state) => {
        state.loading.get = true;
        state.req_success.get = false;
      })
      .addCase(getSuccessStories.fulfilled, (state, { payload }: PayloadAction<any>) => {
        state.loading.get = false;
        state.req_success.get = true;
        state.all_stories = payload?.data?.data || [];
        applyFilters(state);
      })
      .addCase(getSuccessStories.rejected, (state) => {
        state.loading.get = false;
      });

    builder
      .addCase(addSuccessStory.pending, (state) => {
        state.loading.add = true;
        state.req_success.add = false;
      })
      .addCase(addSuccessStory.fulfilled, (state, { payload }: PayloadAction<any>) => {
        state.loading.add = false;
        state.req_success.add = true;
        if (payload?.data) {
          state.all_stories = [payload.data, ...state.all_stories];
          applyFilters(state);
        }
      })
      .addCase(addSuccessStory.rejected, (state) => {
        state.loading.add = false;
      });

    builder
      .addCase(updateSuccessStory.pending, (state) => {
        state.loading.add = true;
        state.req_success.add = false;
      })
      .addCase(updateSuccessStory.fulfilled, (state, { payload }: PayloadAction<any>) => {
        state.loading.add = false;
        state.req_success.add = true;
        state.edit = false;
        state.single = null;
        state.all_stories = state.all_stories.map((s) =>
          s.id === payload?.data?.id ? payload.data : s
        );
        applyFilters(state);
      })
      .addCase(updateSuccessStory.rejected, (state) => {
        state.loading.add = false;
      });

    builder
      .addCase(deleteSuccessStory.pending, (state) => {
        state.loading.delete = true;
        state.req_success.delete = false;
      })
      .addCase(deleteSuccessStory.fulfilled, (state, { payload }: PayloadAction<any>) => {
        state.loading.delete = false;
        state.req_success.delete = true;
        state.all_stories = state.all_stories.filter((s) => s.id !== payload?.id);
        applyFilters(state);
      })
      .addCase(deleteSuccessStory.rejected, (state) => {
        state.loading.delete = false;
      });

    builder
      .addCase(toggleSuccessStoryStatus.pending, (state) => {
        state.loading.toggle = true;
      })
      .addCase(toggleSuccessStoryStatus.fulfilled, (state, { payload }: PayloadAction<any>) => {
        state.loading.toggle = false;
        state.all_stories = state.all_stories.map((s) =>
          s.id === payload?.data?.id ? { ...s, status: payload.data.status } : s
        );
        applyFilters(state);
      })
      .addCase(toggleSuccessStoryStatus.rejected, (state) => {
        state.loading.toggle = false;
      });
  },
});

export const {
  setFilter,
  setSearch,
  editStory,
  closeEditModal,
  reorderStoriesLocal,
} = successStoriesSlice.actions;

export default successStoriesSlice.reducer;
